import React, { useState } from 'react';
import { ExternalLink, Award, Filter } from 'lucide-react';
import { certificateLinks } from '../data/certificateLinks';

interface Certificate {
  id: number;
  title: string;
  issuer: string;
  date: string;
  description: string;
  category: 'all' | 'web-dev' | 'ai' | 'ai-agents' | 'data-science';
  driveLink: string;
  previewImage?: string;
}

const Certificates: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<'all' | 'web-dev' | 'ai' | 'ai-agents' | 'data-science'>('all');

  const certificates: Certificate[] = [
    {
      id: 1,
      title: 'Deep Learning with Keras and TensorFlow',
      issuer: 'Professional Certificate Program',
      date: '2024',
      description: 'Comprehensive training in deep learning architectures, neural networks, and advanced machine learning techniques using Keras and TensorFlow frameworks.',
      category: 'ai',
      driveLink: certificateLinks.deepLearning.driveLink,
      previewImage: certificateLinks.deepLearning.previewImage,
    },
    {
      id: 2,
      title: 'Database Management Systems',
      issuer: 'Certificate Program',
      date: '2024',
      description: 'Gained expertise in database design, SQL optimization, and database management best practices for AI/ML data storage and retrieval systems.',
      category: 'data-science',
      driveLink: certificateLinks.databaseManagement.driveLink,
      previewImage: certificateLinks.databaseManagement.previewImage,
    },
    {
      id: 3,
      title: 'Introduction to Data Mining',
      issuer: 'Certificate Program',
      date: '2024',
      description: 'Learned advanced data mining techniques, pattern recognition, machine learning algorithms, and knowledge discovery methods for intelligent systems.',
      category: 'data-science',
      driveLink: certificateLinks.dataMining.driveLink,
      previewImage: certificateLinks.dataMining.previewImage,
    },
  ];

  const filterOptions = [
    { key: 'all', label: 'All Certificates', count: certificates.length },
    { key: 'web-dev', label: 'Web Development', count: certificates.filter(cert => cert.category === 'web-dev').length },
    { key: 'ai', label: 'Artificial Intelligence', count: certificates.filter(cert => cert.category === 'ai').length },
    { key: 'ai-agents', label: 'AI Agents', count: certificates.filter(cert => cert.category === 'ai-agents').length },
    { key: 'data-science', label: 'Data Science', count: certificates.filter(cert => cert.category === 'data-science').length },
  ];

  const filteredCertificates = activeFilter === 'all'
    ? certificates
    : certificates.filter(cert => cert.category === activeFilter);

  return (
    <section id="certificates" className="py-24 bg-gradient-to-b from-purple-950/5 to-black relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-purple-500/5 rounded-full filter blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full filter blur-3xl animate-float-delay"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold font-orbitron mb-4 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
            Certifications & Achievements
          </h2>
          <div className="h-1 w-20 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full mb-8"></div>
          <p className="max-w-3xl mx-auto text-lg text-gray-300">
            Professional certifications and achievements that validate my expertise in AI/ML, data science, and advanced software engineering technologies.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {filterOptions.map((option) => (
            <button
              key={option.key}
              onClick={() => setActiveFilter(option.key as any)}
              className={`group relative overflow-hidden rounded-full px-6 py-3 font-medium transition-all duration-300 ${activeFilter === option.key
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg shadow-purple-500/25'
                : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 hover:text-white'
                }`}
            >
              <span className="relative z-10 flex items-center">
                <Filter className="h-4 w-4 mr-2" />
                {option.label}
                {option.count > 0 && (
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${activeFilter === option.key
                    ? 'bg-white/20 text-white'
                    : 'bg-purple-900/30 text-purple-300'
                    }`}>
                    {option.count}
                  </span>
                )}
              </span>
              {activeFilter !== option.key && (
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              )}
            </button>
          ))}
        </div>

        {/* Certificates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCertificates.map((certificate, index) => (
            <div
              key={certificate.id}
              className="group relative overflow-hidden rounded-xl bg-gray-900/80 backdrop-blur-sm border border-purple-900/30 hover:border-purple-500/50 transition-all duration-500 hover:-translate-y-2"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Certificate Preview */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={certificate.previewImage}
                  alt={certificate.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>

                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-900/80 text-purple-200 backdrop-blur-sm">
                    <Award className="h-3 w-3 mr-1" />
                    {certificate.category.replace('-', ' ').toUpperCase()}
                  </span>
                </div>

                {/* View Certificate Button */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <a
                    href={certificate.driveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-600/80 hover:bg-purple-500 text-white transition-colors backdrop-blur-sm"
                    title="View Certificate"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>

              {/* Certificate Info */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold font-orbitron text-white group-hover:text-purple-300 transition-colors">
                    {certificate.title}
                  </h3>
                </div>

                <div className="flex items-center text-sm text-gray-400 mb-3">
                  <span className="text-purple-400">{certificate.issuer}</span>
                  <span className="mx-2">•</span>
                  <span>{certificate.date}</span>
                </div>

                <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                  {certificate.description}
                </p>

                {/* Action Button */}
                <div className="flex items-center justify-between">
                  <a
                    href={certificate.driveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors group/link"
                  >
                    View Certificate
                    <ExternalLink className="h-4 w-4 ml-1 transform transition-transform group-hover/link:translate-x-1" />
                  </a>

                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">
                    <Award className="h-4 w-4 text-white" />
                  </div>
                </div>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredCertificates.length === 0 && (
          <div className="text-center py-12">
            <Award className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">No certificates found</h3>
            <p className="text-gray-500">No certificates match the selected filter.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Certificates;